import React, { useState } from 'react';
import { getAllFeatures } from '../services/featureService';
import { lexicalToMarkdown } from '../utils/lexicalUtils';
import ImportFeaturesWizard from './ImportFeaturesWizard';
import './DataManagement.css';

const DataManagement = ({ featureGroupId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showImportWizard, setShowImportWizard] = useState(false);

  const exportFeatures = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get all features for the feature group
      const features = await getAllFeatures(featureGroupId);

      if (!features || features.length === 0) {
        setError('No features found to export');
        return;
      }

      // Convert features to markdown
      let markdown = '';

      const convertFeatureToMarkdown = (feature, level = 1) => {
        // H1 for parent features, H2 for sub-features
        const headingPrefix = level === 1 ? '# ' : '## ';
        markdown += `${headingPrefix}${feature.name}\n\n`;

        // Description as paragraph
        if (feature.description) {
          markdown += `${feature.description}\n\n`;
        }

        // Details (Lexical content) converted to markdown
        if (feature.details) {
          try {
            const detailsMarkdown = lexicalToMarkdown(feature.details);
            if (detailsMarkdown) {
              markdown += `${detailsMarkdown}\n\n`;
            }
          } catch (e) {
            console.error('Error converting details to markdown:', e);
            // Fallback to plain text if conversion fails
            const plainText = typeof feature.details === 'string' 
              ? feature.details 
              : JSON.stringify(feature.details);
            markdown += `${plainText}\n\n`;
          }
        }

        // Status field
        if (feature.status) {
          markdown += `Status: ${feature.status}\n\n`;
        }

        // Recursively process sub-features
        if (feature.subFeatures && feature.subFeatures.length > 0) {
          feature.subFeatures.forEach(subFeature => {
            convertFeatureToMarkdown(subFeature, level + 1);
          });
        }
      };

      // Process all root features
      features.forEach(feature => {
        convertFeatureToMarkdown(feature, 1);
      });

      // Create and download the file
      const blob = new Blob([markdown], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `features-export-${new Date().toISOString().split('T')[0]}.md`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Failed to export features');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="data-management-container">
        <div className="data-management-header">
          <h3>Data Management</h3>
        </div>
        
        <div className="data-management-directions">
          <p>Export your features as a markdown file to include in prompts, or import features from another feature map.</p>
        </div>

        {error && (
          <div className="error-message">
            <i className="fas fa-exclamation-circle"></i> {error}
          </div>
        )}

        <div className="data-management-actions">
          <button
            type="button"
            className="export-button"
            onClick={exportFeatures}
            disabled={loading}
          >
            {loading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> Exporting...
              </>
            ) : (
              <>
                <i className="fas fa-download"></i> Export Full Hierarchy
              </>
            )}
          </button>
          <button
            type="button"
            className="import-button"
            onClick={() => setShowImportWizard(true)}
            disabled={loading}
          >
            <i className="fas fa-upload"></i> Import Features
          </button>
        </div>
      </div>

      {showImportWizard && (
        <ImportFeaturesWizard
          featureGroupId={featureGroupId}
          onClose={() => setShowImportWizard(false)}
          onImportComplete={() => {
            setShowImportWizard(false);
            // Optionally refresh the page or reload features
            window.location.reload();
          }}
        />
      )}
    </>
  );
};

export default DataManagement;

